import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'how-to-buy-telegram-stars',
  title: 'How to Buy Telegram Stars: App Store vs Fragment vs @PremiumBot (Cheapest Way)',
  metaTitle: 'How to Buy Telegram Stars Cheaply: Fragment vs In-App',
  metaDescription:
    'All the ways to buy Telegram Stars in 2026: in-app on iPhone and Android, via @PremiumBot, or on Fragment with TON for the lowest price. Steps, prices, gifting and troubleshooting.',
  keywords: [
    'how to buy telegram stars',
    'buy telegram stars',
    'buy telegram stars cheap',
    'telegram stars fragment',
    'premiumbot stars',
    'gift telegram stars',
    'telegram stars not working',
  ],
  category: 'Guides',
  datePublished: '2026-10-06',
  dateModified: '2026-10-12',
  readingTimeMin: 6,
  excerpt:
    'Need Stars to pay for a bot, a Mini App item or a paid post? You can buy them in three places, and the price differs noticeably between them. Here is how each method works, which one is cheapest and how to fix common purchase problems.',
  keyTakeaways: [
    'You can buy Telegram Stars in-app (App Store / Google Play), through the official @PremiumBot, or on Fragment with TON.',
    'Fragment is usually the cheapest because it avoids app store commissions; in-app is the most convenient.',
    'Stars can be gifted to other users and are spent instantly on bots, Mini Apps, paid media, subscriptions and gifts.',
    'Users cannot withdraw Stars; only sellers who receive Stars can convert them to TON.',
  ],
  body: [
    h2('Where to buy Telegram Stars'),
    table(
      ['Method', 'Payment', 'Price level', 'Best for'],
      [
        ['In-app (iOS / Android)', 'App Store / Google Play', 'Highest', 'Convenience, small amounts'],
        ['@PremiumBot', 'Card / in-app', 'Medium to high', 'Gifting, desktop users'],
        ['Fragment (fragment.com/stars)', 'TON', 'Lowest', 'Larger amounts, crypto users'],
      ],
    ),

    h2('Method 1: Buy Stars in the Telegram app'),
    ol([
      'Open **Settings → My Stars** (or tap any Stars price in a bot, Mini App or channel).',
      'Choose a pack. Larger packs have a lower price per Star.',
      'Confirm with Face ID / Touch ID / Google Play. Stars appear in your balance instantly.',
    ]),
    p('You can also let a purchase trigger it: when you try to pay an invoice without enough Stars, Telegram proposes the exact top-up needed and completes the payment afterwards.'),

    h2('Method 2: Buy or gift Stars with @PremiumBot'),
    p(
      '[@PremiumBot](https://t.me/PremiumBot) is Telegram\'s official bot for Premium and Stars. Send `/stars`, pick a pack and pay. The bot is the easiest way to **gift Stars to a friend**: choose the recipient and the Stars land in their balance with a message.',
    ),

    h2('Method 3: Buy Stars on Fragment with TON (cheapest)'),
    p(
      '[Fragment](https://fragment.com/stars) is Telegram\'s official marketplace. Because purchases are paid in TON outside the app stores, no store commission applies and the price per Star is typically the lowest available.',
    ),
    ol([
      'Go to fragment.com/stars and log in with your Telegram account.',
      'Enter the Telegram username that should receive the Stars (yours or a friend\'s).',
      'Choose the quantity. The TON price is shown live.',
      'Connect a TON wallet (Tonkeeper, Telegram Wallet, MyTonWallet) and confirm the transaction.',
      'Stars are credited to the recipient within a minute.',
    ]),
    callout('No TON yet? Buy some on an exchange or in Telegram\'s Wallet and send it to your TON wallet first. Keep a little extra for network fees.'),

    h2('How much do Telegram Stars cost?'),
    p(
      'In-app prices sit roughly between **$0.015 and $0.025 per Star** depending on the pack and the country. Fragment is cheaper. For a detailed table and the seller-side value, read [How much is a Telegram Star worth?](/blog/telegram-stars-price-usd-ton).',
    ),

    h2('What can you spend Stars on?'),
    ul([
      'Bot services and premium features priced in Stars.',
      'Items and credits in Telegram Mini Apps and games.',
      'Paid posts, paid media and channel subscriptions.',
      'Gifts and paid reactions for creators.',
      'Paid messages to accounts that enable them.',
    ]),
    p('If you are the one selling, see [How to accept Telegram Stars payments](/blog/how-to-accept-telegram-stars-payments).'),

    h2('Can you refund or withdraw Stars you bought?'),
    p(
      'Stars are not withdrawable by users and cannot be converted back to money. If a purchase in a bot goes wrong, the seller can refund the Stars to your balance; app store purchases of Stars themselves follow the store\'s refund policy. Buy the amount you intend to spend.',
    ),

    h2('Troubleshooting Stars purchases'),
    ul([
      '**Purchase button missing**: update Telegram; Stars require a recent version.',
      '**Payment declined in-app**: check your App Store / Google Play payment method and region.',
      '**Fragment says "username not found"**: the recipient must have a public username set in Telegram.',
      '**Stars not credited**: wait a minute and restart the app; on Fragment, check the transaction status in your wallet.',
      '**Cannot pay an invoice**: the invoice may be expired or disabled by the seller; ask for a new link.',
    ]),
  ],
  faq: [
    {
      q: 'What is the cheapest way to buy Telegram Stars?',
      a: 'Fragment (fragment.com/stars), paid in TON, is usually the cheapest because no app store commission applies.',
    },
    {
      q: 'Can I buy Telegram Stars with a credit card?',
      a: 'Yes, through in-app purchase on iOS and Android or via @PremiumBot. Fragment requires TON.',
    },
    {
      q: 'Can I gift Telegram Stars?',
      a: 'Yes. Use @PremiumBot or Fragment and enter the recipient\'s username.',
    },
    {
      q: 'Can I get my money back for unused Stars?',
      a: 'Not from Telegram. Stars are non-withdrawable for users. Sellers can refund specific purchases in Stars.',
    },
  ],
  relatedSlugs: [
    'telegram-stars-price-usd-ton',
    'what-are-telegram-stars',
    'how-to-accept-telegram-stars-payments',
  ],
};

export default article;
